/**
 * QBridgeSetupModal Component (E8)
 * 4-step wizard for connecting MT5 via QBridge
 *
 * Design: Institutional-grade, retail-friendly
 * Pattern: Matches PricingModal styling exactly
 *
 * Steps:
 * 1. Welcome - Benefits overview
 * 2. Download - QBridge download
 * 3. Install - Installation guide
 * 4. Success - Confirmation
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComputerIcon from '@mui/icons-material/Computer';
import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EmailIcon from '@mui/icons-material/Email';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS - Matching existing design system
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  background: '#0F0F14',
  surface: 'rgba(255, 255, 255, 0.02)',
  surfaceHover: 'rgba(255, 255, 255, 0.04)',
  border: 'rgba(255, 255, 255, 0.1)',
  borderLight: 'rgba(255, 255, 255, 0.08)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  primary: '#27B56D',
  primaryLight: 'rgba(39, 181, 109, 0.1)',
  primaryBorder: 'rgba(39, 181, 109, 0.3)',
  accent: '#7c3aed',
  accentLight: 'rgba(124, 58, 237, 0.1)',
  error: '#EF4444'
};

const STEPS = ['Welcome', 'Download', 'Install', 'Done'];

// localStorage keys
const STORAGE_KEYS = {
  DISMISSED: 'qbridge_setup_dismissed',
  DISMISSED_AT: 'qbridge_setup_dismissed_at',
  CONNECTED: 'qbridge_connected',
  SKIP_COUNT: 'qbridge_skip_count'
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * QBridgeSetupModal
 * @param {boolean} open - Modal visibility
 * @param {function} onClose - Close handler
 * @param {function} onSkip - Skip handler (optional)
 * @param {function} onComplete - Completion handler
 * @param {string} userEmail - User's email for QBridge linking
 */
const QBridgeSetupModal = ({
  open = false,
  onClose,
  onSkip,
  onComplete,
  userEmail = ''
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setDownloadComplete(false);
      setConnectionStatus(null);
    }
  }, [open]);

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSkip = () => {
    // Record skip in localStorage with timestamp
    localStorage.setItem(STORAGE_KEYS.DISMISSED, 'true');
    localStorage.setItem(STORAGE_KEYS.DISMISSED_AT, Date.now().toString());

    // Increment skip count
    const skipCount = parseInt(localStorage.getItem(STORAGE_KEYS.SKIP_COUNT) || '0', 10);
    localStorage.setItem(STORAGE_KEYS.SKIP_COUNT, (skipCount + 1).toString());

    onSkip?.();
    onClose?.();
  };

  const handleContinueWithoutMT5 = () => {
    // Same as skip but with different UX intent
    handleSkip();
  };

  const handleDownload = async () => {
    setIsDownloading(true);

    // Simulate download start (replace with real API call)
    // In production: const info = await getQBridgeDownload();
    setTimeout(() => {
      // Open download link (mock URL for now)
      window.open('https://cdn.quantx.ai/qbridge/QBridge-Setup-1.0.0.exe', '_blank');
      setIsDownloading(false);
      setDownloadComplete(true);
    }, 1000);
  };

  const handleCheckConnection = async () => {
    setIsChecking(true);
    setConnectionStatus(null);

    // Simulate connection check (replace with real API call)
    // In production: const status = await getSyncStatus(token);
    setTimeout(() => {
      // Mock: randomly succeed or fail for testing
      const success = Math.random() > 0.3;
      setConnectionStatus(success ? 'connected' : 'failed');
      setIsChecking(false);

      if (success) {
        localStorage.setItem(STORAGE_KEYS.CONNECTED, 'true');
        setActiveStep(3); // Move to success step
      }
    }, 2000);
  };

  const handleComplete = () => {
    onComplete?.();
    onClose?.();
  };

  const handleAskHelp = () => {
    // Close modal and trigger help prompt in chat
    onClose?.();
    // The parent component should handle sending "Help me install QBridge" to chat
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: COLORS.background,
          backgroundImage: 'none',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          maxWidth: '560px',
          overflow: 'hidden'
        }
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${COLORS.borderLight}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* MT5 Logo Placeholder */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, #22C55E 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 20px ${COLORS.primaryLight}`
            }}
          >
            <LinkIcon sx={{ color: '#FFFFFF', fontSize: '1.5rem' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: '1.125rem',
                fontWeight: 600,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              Connect Your MT5
            </Typography>
            <Typography
              sx={{
                color: COLORS.textMuted,
                fontSize: '0.8125rem'
              }}
            >
              Get personalized AI insights from your trades
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: COLORS.textMuted,
            '&:hover': {
              color: COLORS.text,
              backgroundColor: COLORS.surfaceHover
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ════════════════════════════════════════════════════════════════════════
          STEPPER
          ════════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: index <= activeStep ? COLORS.primary : COLORS.textMuted,
                    '&.Mui-completed': { color: COLORS.primary },
                    '&.Mui-active': { color: COLORS.primary }
                  }
                }}
              >
                <Typography
                  sx={{
                    color: index <= activeStep ? COLORS.text : COLORS.textMuted,
                    fontSize: '0.75rem',
                    fontWeight: index === activeStep ? 600 : 400
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogContent sx={{ padding: '16px 24px 24px' }}>
        {activeStep === 0 && (
          <StepWelcome
            onNext={handleNext}
            onSkip={handleSkip}
            onContinueWithoutMT5={handleContinueWithoutMT5}
          />
        )}
        {activeStep === 1 && (
          <StepDownload
            onNext={handleNext}
            onBack={handleBack}
            onDownload={handleDownload}
            isDownloading={isDownloading}
            downloadComplete={downloadComplete}
            onAskHelp={handleAskHelp}
          />
        )}
        {activeStep === 2 && (
          <StepInstall
            onBack={handleBack}
            onCheckConnection={handleCheckConnection}
            isChecking={isChecking}
            connectionStatus={connectionStatus}
            userEmail={userEmail}
            onAskHelp={handleAskHelp}
          />
        )}
        {activeStep === 3 && (
          <StepSuccess onComplete={handleComplete} />
        )}
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 1: WELCOME
// ═══════════════════════════════════════════════════════════════════════════════

const StepWelcome = ({ onNext, onSkip, onContinueWithoutMT5 }) => {
  const benefits = [
    { icon: <TrendingUpIcon />, text: 'AI analysis of your actual trading performance' },
    { icon: <SecurityIcon />, text: 'Personalized recommendations based on your history' },
    { icon: <ComputerIcon />, text: 'EA strategy breakdowns by magic number' }
  ];

  return (
    <Box>
      {/* Benefits */}
      <Box
        sx={{
          backgroundColor: COLORS.primaryLight,
          border: `1px solid ${COLORS.primaryBorder}`,
          borderRadius: '12px',
          padding: '16px',
          mb: 2
        }}
      >
        <Typography
          sx={{
            color: COLORS.text,
            fontSize: '0.875rem',
            fontWeight: 600,
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CheckCircleIcon sx={{ color: COLORS.primary, fontSize: '1.125rem' }} />
          What you'll get
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {benefits.map((benefit, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: COLORS.primary, display: 'flex' }}>
                {React.cloneElement(benefit.icon, { sx: { fontSize: '1rem' } })}
              </Box>
              <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8125rem' }}>
                {benefit.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Setup time */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          color: COLORS.textMuted
        }}
      >
        <AccessTimeIcon sx={{ fontSize: '1rem' }} />
        <Typography sx={{ fontSize: '0.8125rem' }}>
          Setup time: ~2 minutes
        </Typography>
      </Box>

      {/* Requirements */}
      <Box
        sx={{
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.borderLight}`,
          borderRadius: '12px',
          padding: '16px',
          mb: 3
        }}
      >
        <Typography
          sx={{
            color: COLORS.textSecondary,
            fontSize: '0.8125rem',
            fontWeight: 600,
            mb: 1
          }}
        >
          Requirements
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <RequirementRow icon="🪟" text="Windows 10 or 11" />
          <RequirementRow icon="📊" text="MetaTrader 5 installed" />
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          onClick={onNext}
          fullWidth
          sx={{
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, #22C55E 100%)`,
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
            padding: '12px 24px',
            borderRadius: '10px',
            boxShadow: `0 4px 16px ${COLORS.primaryLight}`,
            '&:hover': {
              background: `linear-gradient(135deg, #22C55E 0%, ${COLORS.primary} 100%)`,
              boxShadow: `0 6px 20px rgba(39, 181, 109, 0.4)`
            }
          }}
        >
          Let's Connect My MT5
        </Button>

        <Button
          onClick={onSkip}
          fullWidth
          sx={{
            color: COLORS.textMuted,
            textTransform: 'none',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: COLORS.surfaceHover,
              color: COLORS.textSecondary
            }
          }}
        >
          Skip for Now
        </Button>
      </Box>

      {/* Continue without MT5 */}
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${COLORS.borderLight}`,
          textAlign: 'center'
        }}
      >
        <Typography sx={{ color: COLORS.textMuted, fontSize: '0.8125rem', mb: 0.5 }}>
          Don't have MT5?
        </Typography>
        <Button
          onClick={onContinueWithoutMT5}
          sx={{
            color: COLORS.primary,
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: COLORS.primaryLight
            }
          }}
        >
          Continue without MT5 →
        </Button>
        <Typography sx={{ color: COLORS.textMuted, fontSize: '0.75rem', mt: 0.5 }}>
          Q still works! You can ask trading questions without connecting.
        </Typography>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 2: DOWNLOAD
// ═══════════════════════════════════════════════════════════════════════════════

const StepDownload = ({
  onNext,
  onBack,
  onDownload,
  isDownloading,
  downloadComplete,
  onAskHelp
}) => {
  return (
    <Box>
      {/* Download Card */}
      <Box
        sx={{
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          mb: 2
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${COLORS.accent} 0%, #a855f7 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}
        >
          <DownloadIcon sx={{ color: '#FFFFFF', fontSize: '2rem' }} />
        </Box>

        <Typography
          sx={{
            color: COLORS.text,
            fontSize: '1rem',
            fontWeight: 600,
            mb: 0.5
          }}
        >
          QBridge v1.0.0
        </Typography>

        <Typography
          sx={{
            color: COLORS.textMuted,
            fontSize: '0.8125rem',
            mb: 2
          }}
        >
          15 MB • Windows 10/11
        </Typography>

        <Button
          onClick={onDownload}
          disabled={isDownloading}
          fullWidth
          startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          sx={{
            background: `linear-gradient(135deg, ${COLORS.accent} 0%, #a855f7 100%)`,
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
            padding: '12px 24px',
            borderRadius: '10px',
            '&:hover': {
              background: `linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)`
            },
            '&.Mui-disabled': {
              background: 'rgba(124, 58, 237, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {isDownloading ? 'Downloading...' : 'Download for Windows'}
        </Button>

        {downloadComplete && (
          <Typography
            sx={{
              color: COLORS.primary,
              fontSize: '0.8125rem',
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5
            }}
          >
            <CheckCircleIcon sx={{ fontSize: '1rem' }} />
            Download started! Check your Downloads folder
          </Typography>
        )}
      </Box>

      {/* Security Info */}
      <Box
        sx={{
          backgroundColor: COLORS.primaryLight,
          border: `1px solid ${COLORS.primaryBorder}`,
          borderRadius: '10px',
          padding: '12px 16px',
          mb: 3
        }}
      >
        <Typography
          sx={{
            color: COLORS.text,
            fontSize: '0.8125rem',
            fontWeight: 600,
            mb: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75
          }}
        >
          <SecurityIcon sx={{ color: COLORS.primary, fontSize: '1rem' }} />
          QBridge is safe
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.75rem' }}>
            • Runs only on YOUR computer
          </Typography>
          <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.75rem' }}>
            • Never sees your MT5 password
          </Typography>
          <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.75rem' }}>
            • Only reads trade history (read-only)
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          onClick={onBack}
          sx={{
            color: COLORS.textSecondary,
            textTransform: 'none',
            '&:hover': { backgroundColor: COLORS.surfaceHover }
          }}
        >
          ← Back
        </Button>
        <Button
          onClick={onNext}
          disabled={!downloadComplete}
          sx={{
            color: downloadComplete ? COLORS.primary : COLORS.textMuted,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: COLORS.primaryLight }
          }}
        >
          I've Downloaded →
        </Button>
      </Box>

      {/* Help Link */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          onClick={onAskHelp}
          startIcon={<HelpOutlineIcon sx={{ fontSize: '1rem' }} />}
          sx={{
            color: COLORS.textMuted,
            textTransform: 'none',
            fontSize: '0.8125rem',
            '&:hover': { color: COLORS.primary, backgroundColor: 'transparent' }
          }}
        >
          Having trouble? Ask Q for help
        </Button>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 3: INSTALL
// ═══════════════════════════════════════════════════════════════════════════════

const StepInstall = ({
  onBack,
  onCheckConnection,
  isChecking,
  connectionStatus,
  userEmail,
  onAskHelp
}) => {
  const steps = [
    {
      icon: <FolderOpenIcon />,
      title: 'Open your Downloads folder',
      description: 'Find "QBridge-Setup.exe"'
    },
    {
      icon: <PlayArrowIcon />,
      title: 'Double-click to run',
      description: 'Click "Yes" if Windows asks permission'
    },
    {
      icon: <ComputerIcon />,
      title: 'Make sure MT5 is running',
      description: 'QBridge needs MT5 open to read your trades'
    },
    {
      icon: <EmailIcon />,
      title: 'Enter your Q AI email in QBridge',
      description: userEmail || 'This links your trading data to your Q account'
    }
  ];

  return (
    <Box>
      {/* Installation Steps */}
      <Box sx={{ mb: 3 }}>
        {steps.map((step, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: index < steps.length - 1 ? 2 : 0
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: COLORS.primaryLight,
                border: `1px solid ${COLORS.primaryBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Typography
                sx={{
                  color: COLORS.primary,
                  fontSize: '0.875rem',
                  fontWeight: 700
                }}
              >
                {index + 1}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: COLORS.text,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  mb: 0.25
                }}
              >
                {step.title}
              </Typography>
              <Typography sx={{ color: COLORS.textMuted, fontSize: '0.8125rem' }}>
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Connection Check */}
      <Box
        sx={{
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          padding: '16px',
          mb: 2
        }}
      >
        {connectionStatus === null && !isChecking && (
          <>
            <Typography
              sx={{
                color: COLORS.textSecondary,
                fontSize: '0.8125rem',
                mb: 2,
                textAlign: 'center'
              }}
            >
              After completing the steps above, click to verify your connection:
            </Typography>
            <Button
              onClick={onCheckConnection}
              fullWidth
              sx={{
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, #22C55E 100%)`,
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9375rem',
                padding: '12px',
                borderRadius: '10px',
                '&:hover': {
                  boxShadow: `0 4px 16px ${COLORS.primaryLight}`
                }
              }}
            >
              I've Connected → Check Now
            </Button>
          </>
        )}

        {isChecking && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={32} sx={{ color: COLORS.primary, mb: 1.5 }} />
            <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.875rem' }}>
              Checking your connection...
            </Typography>
          </Box>
        )}

        {connectionStatus === 'failed' && (
          <Box>
            <Typography
              sx={{
                color: COLORS.error,
                fontSize: '0.875rem',
                fontWeight: 600,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75
              }}
            >
              <CloseIcon sx={{ fontSize: '1.125rem' }} />
              Connection not found
            </Typography>
            <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8125rem', mb: 2 }}>
              Make sure:
            </Typography>
            <Box sx={{ color: COLORS.textMuted, fontSize: '0.8125rem', mb: 2 }}>
              <Typography sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                ✓ QBridge is running on your PC
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                ✓ MT5 is open
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem' }}>
                ✓ You used the same email: {userEmail || 'your Q AI email'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={onCheckConnection}
                sx={{
                  flex: 1,
                  backgroundColor: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.text,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: COLORS.surfaceHover }
                }}
              >
                Try Again
              </Button>
              <Button
                onClick={onAskHelp}
                sx={{
                  flex: 1,
                  backgroundColor: COLORS.primaryLight,
                  border: `1px solid ${COLORS.primaryBorder}`,
                  color: COLORS.primary,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(39, 181, 109, 0.15)' }
                }}
              >
                Ask Q for Help
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={onBack}
          sx={{
            color: COLORS.textSecondary,
            textTransform: 'none',
            '&:hover': { backgroundColor: COLORS.surfaceHover }
          }}
        >
          ← Back
        </Button>
      </Box>

      {/* Help Link */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          onClick={onAskHelp}
          startIcon={<HelpOutlineIcon sx={{ fontSize: '1rem' }} />}
          sx={{
            color: COLORS.textMuted,
            textTransform: 'none',
            fontSize: '0.8125rem',
            '&:hover': { color: COLORS.primary, backgroundColor: 'transparent' }
          }}
        >
          Need help? Type "Help me install QBridge" in the chat
        </Button>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 4: SUCCESS
// ═══════════════════════════════════════════════════════════════════════════════

const StepSuccess = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    return () => clearInterval(timer);
  }, []);

  const suggestions = [
    '"Analyze my trading performance"',
    '"What\'s my win rate on EURUSD?"',
    '"When do I trade best?"',
    '"Review my EA strategies"'
  ];

  return (
    <Box sx={{ textAlign: 'center' }}>
      {/* Success Icon */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, #22C55E 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          boxShadow: `0 0 40px ${COLORS.primaryLight}`
        }}
      >
        <CheckCircleIcon sx={{ color: '#FFFFFF', fontSize: '2.5rem' }} />
      </Box>

      <Typography
        sx={{
          color: COLORS.text,
          fontSize: '1.25rem',
          fontWeight: 700,
          mb: 1
        }}
      >
        You're All Set!
      </Typography>

      <Typography
        sx={{
          color: COLORS.textMuted,
          fontSize: '0.875rem',
          mb: 3
        }}
      >
        Your MT5 trading history is syncing now.
      </Typography>

      {/* Progress */}
      <Box
        sx={{
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          padding: '16px',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8125rem' }}>
            Importing trades...
          </Typography>
          <Typography sx={{ color: COLORS.primary, fontSize: '0.8125rem', fontWeight: 600 }}>
            {progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: COLORS.borderLight,
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              background: `linear-gradient(90deg, ${COLORS.primary} 0%, #22C55E 100%)`
            }
          }}
        />
      </Box>

      {/* Suggestions */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            color: COLORS.textSecondary,
            fontSize: '0.8125rem',
            fontWeight: 600,
            mb: 1.5
          }}
        >
          What you can ask Q now:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {suggestions.map((suggestion, index) => (
            <Typography
              key={index}
              sx={{
                color: COLORS.textMuted,
                fontSize: '0.8125rem',
                fontStyle: 'italic'
              }}
            >
              • {suggestion}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Complete Button */}
      <Button
        onClick={onComplete}
        fullWidth
        sx={{
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, #22C55E 100%)`,
          color: '#FFFFFF',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          padding: '12px 24px',
          borderRadius: '10px',
          boxShadow: `0 4px 16px ${COLORS.primaryLight}`,
          '&:hover': {
            boxShadow: `0 6px 20px rgba(39, 181, 109, 0.4)`
          }
        }}
      >
        Start Chatting with Q
      </Button>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const RequirementRow = ({ icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography sx={{ fontSize: '1rem' }}>{icon}</Typography>
    <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8125rem' }}>
      {text}
    </Typography>
  </Box>
);

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if QBridge setup modal should be shown
 * @returns {boolean}
 */
export const shouldShowQBridgeSetup = () => {
  // Already connected - don't show
  if (localStorage.getItem(STORAGE_KEYS.CONNECTED) === 'true') {
    return false;
  }

  // Check if dismissed within last 7 days
  const dismissedAt = localStorage.getItem(STORAGE_KEYS.DISMISSED_AT);
  if (dismissedAt) {
    const daysSinceDismissed = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissed < 7) {
      return false;
    }
  }

  return true;
};

/**
 * Mark QBridge as connected
 */
export const markQBridgeConnected = () => {
  localStorage.setItem(STORAGE_KEYS.CONNECTED, 'true');
  localStorage.removeItem(STORAGE_KEYS.DISMISSED);
  localStorage.removeItem(STORAGE_KEYS.DISMISSED_AT);
};

/**
 * Reset QBridge setup state (for testing)
 */
export const resetQBridgeSetup = () => {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
};

export default QBridgeSetupModal;
